/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { cached, tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type AssignmentRuleCandidateModel from 'uplisting-frontend/models/assignment-rule-candidate';
import type { GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import {
  AssignmentRuleKind,
  AssignmentRuleMode,
  type IPausedMember,
  type IRotationEntry,
} from 'uplisting-frontend/models/assignment-rule';

interface IArgs {
  rule: ModelRegistry['assignment-rule'];
  properties: ModelRegistry['property'][];
  rules: ModelRegistry['assignment-rule'][];
  onOpenExisting(rule: ModelRegistry['assignment-rule']): void;
  onClose(): void;
}

interface AssignmentRulePanelSignature {
  Element: HTMLElement;

  Args: IArgs;
}

export default class UiAssignmentRulePanelComponent extends Component<AssignmentRulePanelSignature> {
  @service notifications!: Services['notifications'];
  @service sentry!: Services['sentry'];

  @service('repositories/assignment-rule')
  repository!: Services['repositories/assignment-rule'];

  @service('repositories/assignment-rule-candidate')
  candidateRepository!: Services['repositories/assignment-rule-candidate'];

  @tracked showDeleteModal = false;
  @tracked rotationReordered = false;
  @tracked rotationDraft: IRotationEntry[];
  @tracked pausedDraft: IPausedMember[];
  @tracked invited: AssignmentRuleCandidateModel[] = [];
  @tracked unitsDraft: number[] = [];
  @tracked coversAllUnits = true;
  @tracked isLoadingCandidates = false;

  #loadGeneration = 0;
  #candidatesById = new Map<number, AssignmentRuleCandidateModel>();

  kinds = Object.values(AssignmentRuleKind);
  modes = Object.values(AssignmentRuleMode);

  constructor(owner: Owner, args: IArgs) {
    super(owner, args);

    this.rotationDraft = [...(args.rule.sortedRotation ?? [])];
    this.pausedDraft = [...(args.rule.paused ?? [])];
    this.unitsDraft = [...(args.rule.multiUnitIds ?? [])];
    this.coversAllUnits = this.unitsDraft.length === 0;

    void this.loadCandidates();
  }

  get rule(): ModelRegistry['assignment-rule'] {
    return this.args.rule;
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['assignment-rule']> {
    return this.repository.buildChangeset(this.rule);
  }

  @cached
  get propertyUnits(): ModelRegistry['multi-unit'][] {
    return this.property?.multiUnits ?? [];
  }

  @cached
  get selectedUnits(): ModelRegistry['multi-unit'][] {
    if (this.coversAllUnits) {
      return this.propertyUnits;
    }

    return this.propertyUnits.filter((unit) =>
      this.unitsDraft.includes(Number(unit.id)),
    );
  }

  @cached
  get noUnitsSelected(): boolean {
    return (
      this.propertyUnits.length > 0 &&
      !this.coversAllUnits &&
      this.unitsDraft.length === 0
    );
  }

  @cached
  get isOrderedMode(): boolean {
    return this.changeset.mode === AssignmentRuleMode.ordered;
  }

  @cached
  get isManualMode(): boolean {
    return this.changeset.mode === AssignmentRuleMode.manual;
  }

  @cached
  get property(): ModelRegistry['property'] | undefined {
    return this.args.properties.find(
      (property) => Number(property.id) === this.changeset.propertyId,
    );
  }

  @cached
  get isRotationLocked(): boolean {
    return this.isLoadingCandidates && !this.rule.isPersisted;
  }

  @cached
  get canReorderRotation(): boolean {
    return this.isOrderedMode && !this.isRotationLocked;
  }

  @cached
  get acceptanceOptions(): number[] {
    const max = Math.max(this.rotationDraft.length, 1);

    return Array.from({ length: max }, (_, index) => index + 1);
  }

  @action
  @waitFor
  async handleSelectProperty(
    property: ModelRegistry['property'],
  ): Promise<void> {
    this.changeset.propertyId = Number(property.id);
    this.unitsDraft = [];
    this.coversAllUnits = true;
    this.changeset.multiUnitIds = [];

    if (this.#openExistingRule()) {
      return;
    }

    await this.loadCandidates();
  }

  @action
  @waitFor
  async handleToggleAllUnits(): Promise<void> {
    this.coversAllUnits = !this.coversAllUnits;
    this.unitsDraft = [];
    this.changeset.multiUnitIds = [];

    await this.#applyUnitSelection();
  }

  @action
  @waitFor
  async handleToggleUnit(unit: ModelRegistry['multi-unit']): Promise<void> {
    const id = Number(unit.id);

    if (this.coversAllUnits) {
      this.coversAllUnits = false;
      this.unitsDraft = this.propertyUnits
        .map((record) => Number(record.id))
        .filter((unitId) => unitId !== id);
    } else {
      this.unitsDraft = this.unitsDraft.includes(id)
        ? this.unitsDraft.filter((unitId) => unitId !== id)
        : [...this.unitsDraft, id];

      if (this.unitsDraft.length === this.propertyUnits.length) {
        this.coversAllUnits = true;
        this.unitsDraft = [];
      }
    }

    await this.#applyUnitSelection();
  }

  @action
  @waitFor
  async handleSelectKind(kind: AssignmentRuleKind): Promise<void> {
    this.changeset.kind = kind;

    if (this.#openExistingRule()) {
      return;
    }

    await this.loadCandidates();
  }

  #openExistingRule(): boolean {
    if (this.rule.isPersisted) {
      return false;
    }

    const existing = this.args.rules.find(
      (rule) =>
        rule.isPersisted &&
        rule.propertyId === this.changeset.propertyId &&
        rule.kind === this.changeset.kind &&
        !rule.multiUnitIds.length,
    );

    if (!existing) {
      return false;
    }

    this.args.onOpenExisting(existing);

    return true;
  }

  @action
  @waitFor
  async handleSelectMode(mode: AssignmentRuleMode): Promise<void> {
    const leavingManual =
      this.isManualMode && mode !== AssignmentRuleMode.manual;

    this.changeset.mode = mode;

    if (leavingManual) {
      await this.loadCandidates({ seedRotation: true });
    }
  }

  @action
  handleSelectRequiredAcceptances(count: number): void {
    this.changeset.requiredAcceptances = count;
  }

  @action
  handleDragEnd(): void {
    if (!this.canReorderRotation) {
      return;
    }

    this.rotationReordered = true;
    this.rotationDraft = this.rotationDraft.map((entry) => ({ ...entry }));

    this.#clampRequiredAcceptances();
  }

  @action
  handlePause(entry: IRotationEntry): void {
    if (this.isRotationLocked) {
      return;
    }

    this.rotationDraft = this.rotationDraft.filter((item) => item !== entry);
    this.pausedDraft = [
      ...this.pausedDraft,
      { user_id: entry.user_id, name: entry.name },
    ];

    this.#clampRequiredAcceptances();
  }

  @action
  handleResume(member: IPausedMember): void {
    if (this.isRotationLocked) {
      return;
    }

    this.pausedDraft = this.pausedDraft.filter((item) => item !== member);
    this.rotationDraft = [
      ...this.rotationDraft,
      {
        user_id: member.user_id,
        name: member.name,
        source: 'derived',
        position: this.rotationDraft.length,
        ...this.#offerHistoryFor(member.user_id),
      },
    ];
  }

  @action
  @waitFor
  async handleDelete(): Promise<void> {
    try {
      await this.rule.destroyRecord();

      this.notifications.info();
      this.args.onClose();
    } catch (error) {
      this.#notifyError(error);
    }
  }

  @action
  @waitFor
  async handleSave(): Promise<void> {
    if (this.noUnitsSelected || this.isRotationLocked) {
      return;
    }

    if (!this.isManualMode) {
      this.changeset.inactiveUserIds = this.pausedDraft.map(
        (member) => member.user_id,
      );

      if (this.rotationReordered) {
        this.changeset.rotation = this.rotationDraft.map((entry, position) => ({
          ...entry,
          position,
        }));
      }
    }

    await this.changeset.validate();

    if (this.changeset.isInvalid) {
      return;
    }

    try {
      await this.changeset.save();

      this.notifications.info();
      this.args.onClose();
    } catch (error) {
      this.#notifyError(error);
    }
  }

  #notifyError(error: unknown): void {
    this.sentry.captureException(error);

    const first = (error as { errors?: unknown[] })?.errors?.[0];
    const message =
      typeof first === 'string'
        ? first
        : (first as { detail?: string })?.detail;

    if (message) {
      this.notifications.error(message, true);
    } else {
      this.notifications.error();
    }
  }

  async #applyUnitSelection(): Promise<void> {
    this.changeset.multiUnitIds = [...this.unitsDraft];

    if (this.noUnitsSelected) {
      if (!this.rule.isPersisted) {
        this.rotationDraft = [];
      }

      return;
    }

    await this.loadCandidates();

    if (this.rule.isPersisted) {
      this.#dropUnreachableMembers();
    }
  }

  #applyOfferHistory(): void {
    this.rotationDraft = this.rotationDraft.map((entry) => ({
      ...entry,
      ...this.#offerHistoryFor(entry.user_id),
    }));
  }

  #offerHistoryFor(userId: number): Partial<IRotationEntry> {
    const candidate = this.#candidatesById.get(userId);

    if (!candidate) {
      return {};
    }

    return {
      offers_received: candidate.offersReceived,
      offers_accepted: candidate.offersAccepted,
      average_on_site_minutes: candidate.averageOnSiteMinutes,
    };
  }

  #dropUnreachableMembers(): void {
    const reachable = this.#candidatesById;

    this.rotationDraft = this.rotationDraft.filter((entry) =>
      reachable.has(entry.user_id),
    );
    this.pausedDraft = this.pausedDraft.filter((member) =>
      reachable.has(member.user_id),
    );

    this.#clampRequiredAcceptances();
  }

  @waitFor
  async loadCandidates({ seedRotation = false } = {}): Promise<void> {
    const propertyId = this.changeset.propertyId;
    const kind = this.changeset.kind;

    if (!propertyId || !kind) {
      return;
    }

    const generation = ++this.#loadGeneration;

    this.isLoadingCandidates = true;

    try {
      const candidates = await this.candidateRepository.query({
        property_id: propertyId,
        kind,
        ...(this.unitsDraft.length ? { multi_unit_ids: this.unitsDraft } : {}),
      });

      if (generation !== this.#loadGeneration) {
        return;
      }

      this.invited = candidates
        .slice()
        .filter((candidate) => candidate.source === 'invited');

      this.#candidatesById = new Map(
        candidates
          .filter((candidate) => candidate.source !== 'invited')
          .map((candidate) => [Number(candidate.id), candidate]),
      );

      if (this.rule.isPersisted && !seedRotation) {
        this.#applyOfferHistory();

        return;
      }

      if (!this.rule.isPersisted) {
        this.pausedDraft = [];
      }

      const paused = new Set(this.pausedDraft.map((member) => member.user_id));

      this.rotationReordered = false;
      this.rotationDraft = candidates
        .slice()
        .filter((candidate) => candidate.source !== 'invited')
        .filter((candidate) => !paused.has(Number(candidate.id)))
        .map((candidate) => ({
          user_id: Number(candidate.id),
          name: candidate.name,
          source: candidate.source as IRotationEntry['source'],
          position: 0,
          offers_received: candidate.offersReceived,
          offers_accepted: candidate.offersAccepted,
          average_on_site_minutes: candidate.averageOnSiteMinutes,
        }));

      this.#clampRequiredAcceptances();
    } finally {
      // A superseded request must not unlock the editor while the reload that
      // replaced it is still running.
      if (generation === this.#loadGeneration) {
        this.isLoadingCandidates = false;
      }
    }
  }

  #clampRequiredAcceptances(): void {
    const max = Math.max(this.rotationDraft.length, 1);

    if (this.changeset.requiredAcceptances > max) {
      this.changeset.requiredAcceptances = max;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::AssignmentRule::Panel': typeof UiAssignmentRulePanelComponent;
  }
}
